/**
 * modal de prompt — substitui window.prompt feio + acessivel.
 * controlled: open/onClose. labelOk + onSubmit recebem o valor.
 *
 * uso:
 *   const [open, setOpen] = useState(false);
 *   <PromptModal
 *     open={open}
 *     titulo="motivo da exclusao"
 *     placeholder="..."
 *     labelOk="excluir"
 *     onClose={() => setOpen(false)}
 *     onSubmit={(motivo) => { excluir(motivo); setOpen(false); }}
 *   />
 */

import { useEffect, useRef, useState } from 'react';

interface PromptModalProps {
  readonly open: boolean;
  readonly titulo: string;
  readonly descricao?: string;
  readonly placeholder?: string;
  readonly labelOk?: string;
  readonly labelCancel?: string;
  readonly initialValue?: string;
  readonly minLength?: number;
  readonly maxLength?: number;
  readonly onClose: () => void;
  readonly onSubmit: (valor: string) => void;
}

export const PromptModal = ({
  open,
  titulo,
  descricao,
  placeholder,
  labelOk = 'confirmar',
  labelCancel = 'cancelar',
  initialValue = '',
  minLength = 1,
  maxLength = 500,
  onClose,
  onSubmit,
}: PromptModalProps) => {
  const [valor, setValor] = useState(initialValue);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (open) {
      setValor(initialValue);
      // foco apos render
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [open, initialValue]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const valido = valor.trim().length >= minLength;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-navy-900/50 p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="prompt-modal-title"
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="prompt-modal-title" className="font-display font-semibold text-lg mb-1">
          {titulo}
        </h3>
        {descricao && <p className="text-sm text-navy-500 mb-3">{descricao}</p>}
        <textarea
          ref={inputRef}
          className="input mb-1"
          rows={3}
          value={valor}
          placeholder={placeholder}
          onChange={(e) => setValor(e.target.value)}
          minLength={minLength}
          maxLength={maxLength}
        />
        <p className="hint">
          {valor.trim().length}/{maxLength}
          {minLength > 1 && ` (mín ${minLength})`}
        </p>

        <div className="flex justify-end gap-2 mt-4">
          <button type="button" onClick={onClose} className="btn-ghost text-sm">
            {labelCancel}
          </button>
          <button
            type="button"
            onClick={() => valido && onSubmit(valor.trim())}
            disabled={!valido}
            className="btn-primary text-sm"
          >
            {labelOk}
          </button>
        </div>
      </div>
    </div>
  );
};
